import { useCallback, useEffect, useState } from 'react';
import { usePlacingPluginContext } from '../placing-plugin/PlacingPluginContext';
import { setPlugin } from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { useDispatch, useSelector } from 'react-redux';
import { useSweepCanvas } from '../../../../sweep-canvas/useSweepCanvas';
import { PluginTypes } from '../../../../../types/enums/PluginTypes';
import { OnPluginClickEvent } from '../../../../sweep-canvas/SweepCanvas';
import { usePanels } from '../../../../panels/usePanels';
import { dataManagementPanelType } from '../plugins/data-management/panel/dataManagementPanelType';
import { bantPanelType } from '../plugins/bant/panel/bantPanelType';
import { dynamicPathPanelType } from '../plugins/dynamic-path/panel/dynamicPathPanelType';
import { meddicPanelType } from '../plugins/meddic/panel/meddicPanelType';
import { meddpiccPanelType } from '../plugins/meddpicc/panel/meddpiccPanelType';
import { getPluginButtonId } from '../../../../sweep-canvas/pluginHelpers';
import { selectFunnelMap } from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { useSweepCanvasFitView } from '../../../../sweep-canvas/useSweepCanvasFitView';

const panelTypesByPluginType = {
  [PluginTypes.BANT]: bantPanelType,
  [PluginTypes.MEDDIC]: meddicPanelType,
  [PluginTypes.MEDDPICC]: meddpiccPanelType,
  [PluginTypes.DYNAMIC_PATH]: dynamicPathPanelType,
  [PluginTypes.DATA_MANAGEMENT]: dataManagementPanelType,
};

export const useSfFunnelPlugins = () => {
  const funnelMap = useSelector(selectFunnelMap);
  const dispatch = useDispatch();
  const { maybeOpenPanel } = usePanels();
  const {
    placingPluginType,
    setPlacingPlugin,
    setConfiguringPluginType,
    configuringPluginType,
    isPlacingPlugin,
  } = usePlacingPluginContext();
  const { pluginButtonExists, removePluginButton } = useSweepCanvas();
  const { fitAroundNodes } = useSweepCanvasFitView();
  const [openPluginType, setOpenPluginType] = useState<{
    type: PluginTypes;
    funnelId: string;
  } | null>(null);

  const openPlugin = useCallback(
    ({
      funnelId,
      pluginType,
      objectType,
    }: {
      funnelId: string;
      pluginType: PluginTypes;
      objectType?: string;
    }) => {
      const tryOpenPanel = () => {
        const anchorEl = window.document.getElementById(getPluginButtonId(pluginType, funnelId));

        if (!anchorEl) {
          return false;
        }

        let _objectType = objectType;
        if (!_objectType) {
          _objectType =
            funnelMap.funnelsData[funnelId]?.funnelDetails.leadingObject?.objectName || '';
        }
        if (!_objectType) {
          console.warn(
            `Object type for plugin type ${pluginType} and funnel id ${funnelId} not found`,
          );
          return false;
        }
        const crmOrgId = funnelMap.funnelsData[funnelId]?.crmOrgId;
        if (!crmOrgId) {
          console.warn(
            `CRM organization id for plugin type ${pluginType} and funnel id ${funnelId} not found`,
          );
          return false;
        }

        maybeOpenPanel({
          panelType: panelTypesByPluginType[pluginType],
          data: {
            funnelId,
            anchorEl,
            crmOrgId,
            objectType: _objectType,
          },
          onClose: () => {
            if (configuringPluginType?.pluginType === `${placingPluginType}-${funnelId}`) {
              removePluginButton(funnelId, pluginType);
            }
            setConfiguringPluginType(null);
          },
        });
        return true;
      };

      // Try to open the panel immediately
      const success = tryOpenPanel();

      // If the anchorEl wasn't found, center the canvas around the funnel and try again
      if (!success) {
        fitAroundNodes({
          nodeIds: [funnelId],
          options: { duration: 500 },
          onAnimationEnd: () => {
            // Try again after centering
            const retrySuccess = tryOpenPanel();
            if (!retrySuccess) {
              console.warn(
                `Plugin button for plugin type ${pluginType} and funnel id ${funnelId} not found even after centering`,
              );
            }
          },
        });
      }
    },
    [
      configuringPluginType?.pluginType,
      funnelMap.funnelsData,
      maybeOpenPanel,
      placingPluginType,
      removePluginButton,
      setConfiguringPluginType,
      fitAroundNodes,
    ],
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (openPluginType) {
      timeoutId = setTimeout(() => {
        openPlugin({
          funnelId: openPluginType.funnelId,
          pluginType: openPluginType.type,
        });
        setOpenPluginType(null);
      }, 500);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [openPlugin, openPluginType]);

  const placePlugin = useCallback(
    (funnelId: string) => {
      if (!placingPluginType) return;
      if (pluginButtonExists(funnelId, placingPluginType)) return;

      switch (placingPluginType) {
        case PluginTypes.DATA_MANAGEMENT: {
          setPlacingPlugin(null);

          const plugin: DataManagementPlugin = {
            id: placingPluginType,
            flags: [],
          };

          dispatch(
            setPlugin({
              funnelId,
              plugin,
            }),
          );
          break;
        }
        case PluginTypes.DYNAMIC_PATH: {
          setPlacingPlugin(null);

          const plugin: DynamicPathPlugin = {
            id: placingPluginType,
            stages: [],
          };

          dispatch(
            setPlugin({
              funnelId,
              plugin,
            }),
          );
          break;
        }
        case PluginTypes.BANT:
        case PluginTypes.MEDDIC:
        case PluginTypes.MEDDPICC:
          setPlacingPlugin(null);
          setConfiguringPluginType({ funnelId, pluginType: placingPluginType });

          break;
      }
      setOpenPluginType({ type: placingPluginType, funnelId });
    },
    [dispatch, placingPluginType, pluginButtonExists, setConfiguringPluginType, setPlacingPlugin],
  );

  const onPluginClick: OnPluginClickEvent = useCallback(
    ({ parentId, pluginId, objectType }) => {
      openPlugin({
        funnelId: parentId,
        pluginType: pluginId,
        objectType,
      });
    },
    [openPlugin],
  );

  const unsetConfiguringPluginType = useCallback(() => {
    setConfiguringPluginType(null);
  }, [setConfiguringPluginType]);

  return {
    placePlugin,
    onPluginClick,
    unsetConfiguringPluginType,
    isPlacingPlugin,
    openPlugin,
  };
};
